import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ClipboardDocumentListIcon,
  BookOpenIcon,
  MapIcon,
  ArchiveBoxIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import TemplateBrowser from './TemplateBrowser';
import TemplateDetail from './TemplateDetail';
import TemplateEditor from './TemplateEditor';
import ArchivedTemplates from './ArchivedTemplates';
import ArchiveTemplateModal from './ArchiveTemplateModal';
import CreateJurisdictionBaseModal from './CreateJurisdictionBaseModal';
import { WorkflowDefinitionBrowser } from '../workflow-definitions/WorkflowDefinitionBrowser';
import { WorkflowDefinitionEditor } from '../workflow-definitions/WorkflowDefinitionEditor';
import { WorkflowCoverageDashboard } from '../workflow-definitions/WorkflowCoverageDashboard';
import CaseSubtypeTaxonomyPanel from '../case-subtype-taxonomy/CaseSubtypeTaxonomyPanel';
import type { TaskTemplateListItem } from './templateManagementService';

// ─── Types ───

type PrimaryTab = 'templates' | 'case_subtypes' | 'statutory' | 'coverage' | 'archived';
type TemplateView = 'browser' | 'detail' | 'editor';

interface TabConfig {
  id: PrimaryTab;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  tooltip: string;
  accentGradient: string;
  activeRing: string;
  activeBg: string;
  activeText: string;
  dotColor: string;
  iconColor: string;
}

const TABS: TabConfig[] = [
  {
    id: 'templates',
    label: 'Templates',
    shortLabel: 'Templates',
    icon: ClipboardDocumentListIcon,
    tooltip: 'Browse jurisdiction-aware templates synthesized from source authorities and business rules',
    accentGradient: 'from-lex-accent to-lex-accent-hover',
    activeRing: 'ring-lex-accent/20',
    activeBg: 'bg-lex-accent-soft dark:bg-lex-accent-soft',
    activeText: 'text-lex-accent dark:text-lex-accent',
    dotColor: 'bg-lex-accent',
    iconColor: 'text-lex-accent dark:text-lex-accent',
  },
  {
    id: 'case_subtypes',
    label: 'Case Subtypes',
    shortLabel: 'Subtypes',
    icon: Squares2X2Icon,
    tooltip: 'Manage practice area → case subtype mappings per jurisdiction.',
    accentGradient: 'from-violet-500 via-indigo-500 to-sky-500',
    activeRing: 'ring-violet-500/20',
    activeBg: 'bg-violet-50 dark:bg-violet-900/30',
    activeText: 'text-violet-700 dark:text-violet-300',
    dotColor: 'bg-violet-500',
    iconColor: 'text-violet-600 dark:text-violet-400',
  },
  {
    id: 'statutory',
    label: 'Statutory Library',
    shortLabel: 'Statutory',
    icon: BookOpenIcon,
    tooltip: 'Author and manage rule-backed workflow definitions with source citations.',
    accentGradient: 'from-lex-accent to-lex-accent-hover',
    activeRing: 'ring-lex-accent/20',
    activeBg: 'bg-lex-accent-soft dark:bg-lex-accent-soft',
    activeText: 'text-lex-accent dark:text-lex-accent',
    dotColor: 'bg-lex-accent',
    iconColor: 'text-lex-accent dark:text-lex-accent',
  },
  {
    id: 'coverage',
    label: 'Coverage Matrix',
    shortLabel: 'Coverage',
    icon: MapIcon,
    tooltip: 'Visualize which state + practice area combinations have published statutory definitions',
    accentGradient: 'from-emerald-500 via-teal-400 to-cyan-400',
    activeRing: 'ring-emerald-500/20',
    activeBg: 'bg-emerald-50 dark:bg-emerald-900/30',
    activeText: 'text-emerald-700 dark:text-emerald-300',
    dotColor: 'bg-emerald-500',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  {
    id: 'archived',
    label: 'Archived',
    shortLabel: 'Archived',
    icon: ArchiveBoxIcon,
    tooltip: 'View and restore archived task templates',
    accentGradient: 'from-amber-500 via-orange-400 to-red-400',
    activeRing: 'ring-amber-500/20',
    activeBg: 'bg-amber-50 dark:bg-amber-900/30',
    activeText: 'text-amber-700 dark:text-amber-300',
    dotColor: 'bg-amber-500',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
];

// ─── Tooltip Component ───

const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined as unknown as ReturnType<typeof setTimeout>);

  const show = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setCoords({ x: rect.left + rect.width / 2, y: rect.bottom + 10 });
      }
      setVisible(true);
    }, 450);
  }, []);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
  }, []);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return (
    <div ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} className="relative">
      {children}
      {visible && (
        <div
          className="fixed z-[100] max-w-xs px-3 py-2.5 text-xs font-medium leading-relaxed text-white bg-gray-900 dark:bg-gray-600 rounded-lg shadow-xl pointer-events-none"
          style={{
            left: coords.x,
            top: coords.y,
            transform: 'translateX(-50%)',
            animation: 'tooltipIn 200ms ease-out forwards',
          }}
        >
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-600 rotate-45" />
          {text}
        </div>
      )}
    </div>
  );
};

// ─── Sliding Indicator Hook ───

function useSlidingIndicator(activeTab: PrimaryTab) {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    const el = tabRefs.current[activeTab];
    const container = containerRef.current;
    if (el && container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      setIndicator({
        left: elRect.left - containerRect.left,
        width: elRect.width,
      });
    }
  }, [activeTab]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const setRef = useCallback((id: string) => (el: HTMLButtonElement | null) => {
    tabRefs.current[id] = el;
  }, []);

  return { indicator, setRef, containerRef };
}

// ─── Header Descriptions ───

const HEADER_INFO: Record<PrimaryTab, { title: string; subtitle: string; description: string }> = {
  templates: {
    title: 'Task Templates',
    subtitle: 'Jurisdiction-Aware Workflows',
    description: 'Browse templates synthesized from source authorities and business rules. Create subscriber variants tailored to your work.',
  },
  case_subtypes: {
    title: 'Case Subtypes',
    subtitle: 'Taxonomy Governance',
    description: 'Manage Practice Area → Case Subtype mappings per jurisdiction. Use Discover from URL to propose pending mappings semi-manually.',
  },
  statutory: {
    title: 'Statutory Library',
    subtitle: 'Source Authority Database',
    description: 'Author, validate, and publish rule-backed workflow definitions with source citations. Changes here flow downstream to all synthesized templates.',
  },
  coverage: {
    title: 'Coverage Matrix',
    subtitle: 'Publication Status Overview',
    description: 'Visualize statutory coverage across states and practice areas. Identify gaps and track publication status at a glance.',
  },
  archived: {
    title: 'Archived Templates',
    subtitle: 'Archive Management',
    description: 'View, restore, or permanently delete archived task templates. Archived templates cannot be used to create new tasks.',
  },
};

// ─── Inline keyframe styles ───

const KEYFRAMES_STYLE = `
@keyframes tooltipIn {
  from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}
@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeSlideLeft {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes pulseOnce {
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
`;

// ─── Main Panel ───

const TaskTemplatesPanel: React.FC = () => {
  // Primary tab state
  const [activeTab, setActiveTab] = useState<PrimaryTab>('templates');
  const [transitioning, setTransitioning] = useState(false);
  const [contentVisible, setContentVisible] = useState(true);
  const [renderKey, setRenderKey] = useState(0);

  // Template sub-views
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateView, setTemplateView] = useState<TemplateView>('browser');

  // Statutory sub-views
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string | null>(null);
  const [statutoryView, setStatutoryView] = useState<'browser' | 'editor'>('browser');
  const [editorInitialState, setEditorInitialState] = useState<string | undefined>(undefined);
  const [editorInitialPA, setEditorInitialPA] = useState<string | undefined>(undefined);

  // Archive modal
  const [archiveTarget, setArchiveTarget] = useState<TaskTemplateListItem | null>(null);
  // Create base modal
  const [showCreateBaseModal, setShowCreateBaseModal] = useState(false);

  // When true, switchTab skips resetting statutory sub-views (set by cross-tab nav)
  const skipStatutoryResetRef = useRef(false);

  const { indicator, setRef, containerRef } = useSlidingIndicator(activeTab);
  const headerInfo = HEADER_INFO[activeTab];
  const activeConfig = TABS.find((t) => t.id === activeTab)!;

  // ─── Tab switching with crossfade ───

  const switchTab = useCallback((tab: PrimaryTab) => {
    if (tab === activeTab || transitioning) return;
    setTransitioning(true);
    setContentVisible(false);

    setTimeout(() => {
      setActiveTab(tab);
      setRenderKey((k) => k + 1);
      // Reset sub-views when switching tabs — unless cross-tab nav pre-set them
      if (tab === 'templates') {
        setSelectedTemplateId(null);
        setTemplateView('browser');
      }
      if (tab === 'statutory' && !skipStatutoryResetRef.current) {
        setSelectedDefinitionId(null);
        setStatutoryView('browser');
        setEditorInitialState(undefined);
        setEditorInitialPA(undefined);
      }
      skipStatutoryResetRef.current = false;
      // Let React render the new content, then fade in
      requestAnimationFrame(() => {
        setContentVisible(true);
        setTransitioning(false);
      });
    }, 180);
  }, [activeTab, transitioning]);

  // ─── Template navigation ───

  const openTemplateDetail = useCallback((id: string) => {
    setSelectedTemplateId(id);
    setTemplateView('detail');
  }, []);

  const openTemplateEditor = useCallback((id: string) => {
    setSelectedTemplateId(id);
    setTemplateView('editor');
  }, []);

  const returnToTemplateBrowser = useCallback(() => {
    setSelectedTemplateId(null);
    setTemplateView('browser');
  }, []);

  // ─── Statutory navigation ───

  const openDefinitionEditor = useCallback((id: string) => {
    setSelectedDefinitionId(id);
    setStatutoryView('editor');
  }, []);

  const openNewDefinition = useCallback(() => {
    setSelectedDefinitionId(null);
    setStatutoryView('editor');
  }, []);

  const returnToDefinitionBrowser = useCallback(() => {
    setSelectedDefinitionId(null);
    setStatutoryView('browser');
  }, []);

  // ─── Archive handling ───

  const handleArchiveRequest = useCallback((template: TaskTemplateListItem) => {
    setArchiveTarget(template);
  }, []);

  const handleArchived = useCallback(() => {
    setArchiveTarget(null);
    // Return to browser and refresh (by resetting view)
    setSelectedTemplateId(null);
    setTemplateView('browser');
  }, []);

  return (
    <>
      {/* Inject keyframe animations */}
      <style>{KEYFRAMES_STYLE}</style>

      <div className="space-y-5">
        {/* ─── Header Card ─── */}
        <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          {/* Accent gradient top bar — color shifts with active tab */}
          <div
            className={`h-1 w-full bg-gradient-to-r ${activeConfig.accentGradient} transition-all duration-500 ease-in-out`}
          />

          <div className="px-5 pt-5 pb-4">
            {/* ─── Title + Description ─── */}
            <div className="flex items-start gap-3.5 mb-5">
              <div
                className={`mt-0.5 p-2.5 rounded-xl transition-colors duration-300 ${activeConfig.activeBg}`}
              >
                <activeConfig.icon
                  className={`h-5 w-5 transition-colors duration-300 ${activeConfig.iconColor}`}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2.5">
                  <h2
                    key={`title-${renderKey}`}
                    className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                    style={{ animation: 'fadeSlideLeft 300ms ease-out forwards' }}
                  >
                    {headerInfo.title}
                  </h2>
                  <span
                    key={`badge-${renderKey}`}
                    className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${activeConfig.activeBg} ${activeConfig.activeText} transition-colors duration-300`}
                    style={{ animation: 'fadeSlideLeft 300ms ease-out 100ms forwards', opacity: 0 }}
                  >
                    {headerInfo.subtitle}
                  </span>
                </div>
                <p
                  key={`desc-${renderKey}`}
                  className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl"
                  style={{ animation: 'fadeSlideLeft 300ms ease-out 50ms forwards', opacity: 0 }}
                >
                  {headerInfo.description}
                </p>
              </div>
              {activeTab === 'templates' && templateView === 'browser' && (
                <button
                  onClick={() => setShowCreateBaseModal(true)}
                  className="ml-auto shrink-0 rounded-lg border border-lex-accent dark:border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft px-3 py-1.5 text-sm font-medium text-lex-accent dark:text-lex-accent hover:bg-lex-state-hover dark:hover:bg-lex-state-hover transition-colors"
                >
                  + Create Base
                </button>
              )}
            </div>

            {/* ─── Tab Bar ─── */}
            <div
              ref={containerRef}
              className="flex items-center gap-1 bg-gray-100/80 dark:bg-gray-900/50 rounded-xl p-1.5 relative"
              role="tablist"
              aria-label="Workflow management sections"
            >
              {/* Sliding background pill */}
              {indicator.width > 0 && (
                <div
                  className={`absolute top-1.5 h-[calc(100%-12px)] rounded-lg bg-white dark:bg-gray-700 shadow-sm ring-1 ${activeConfig.activeRing} transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] pointer-events-none`}
                  style={{ left: `${indicator.left}px`, width: `${indicator.width}px` }}
                  aria-hidden="true"
                />
              )}

              {TABS.map((tab) => {
                const isActive = tab.id === activeTab;
                const Icon = tab.icon;

                return (
                  <Tooltip key={tab.id} text={tab.tooltip}>
                    <button
                      ref={setRef(tab.id)}
                      onClick={() => switchTab(tab.id)}
                      disabled={transitioning}
                      className={`relative z-10 flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 select-none whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-lex-accent ${
                        isActive
                          ? tab.activeText
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      } ${transitioning ? 'pointer-events-none' : ''}`}
                      aria-selected={isActive}
                      role="tab"
                      aria-controls={`panel-${tab.id}`}
                      tabIndex={isActive ? 0 : -1}
                    >
                      <Icon className={`h-4 w-4 transition-colors duration-200 ${isActive ? tab.iconColor : ''}`} />
                      {/* Full label on sm+, short on mobile */}
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.shortLabel}</span>
                      {/* Active dot pulse for extra feedback */}
                      {isActive && (
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${tab.dotColor}`}
                          style={{ animation: 'pulseOnce 300ms ease-out forwards' }}
                        />
                      )}
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>

        {/* ─── Content Area with Crossfade ─── */}
        <div
          id={`panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={activeTab}
          className="transition-all duration-200 ease-in-out"
          style={{
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? 'translateY(0)' : 'translateY(6px)',
          }}
        >
          {/* ─── Templates Tab ─── */}
          {activeTab === 'templates' && (
            <>
              {templateView === 'detail' && selectedTemplateId ? (
                <TemplateDetail
                  templateId={selectedTemplateId}
                  onBack={returnToTemplateBrowser}
                  onEditTemplate={openTemplateEditor}
                  onTemplateForked={openTemplateEditor}
                  onArchiveTemplate={handleArchiveRequest}
                />
              ) : templateView === 'editor' && selectedTemplateId ? (
                <TemplateEditor
                  templateId={selectedTemplateId}
                  onCancel={() => setTemplateView('detail')}
                  onSaved={(savedId) => {
                    setSelectedTemplateId(savedId);
                    setTemplateView('detail');
                  }}
                />
              ) : (
                <TemplateBrowser
                  onSelectTemplate={openTemplateDetail}
                  onEditTemplate={openTemplateEditor}
                  onArchiveTemplate={handleArchiveRequest}
                />
              )}
            </>
          )}

          {/* ─── Case Subtypes Tab ─── */}
          {activeTab === 'case_subtypes' && (
            <CaseSubtypeTaxonomyPanel />
          )}

          {/* ─── Statutory Library Tab ─── */}
          {activeTab === 'statutory' && (
            <>
              {statutoryView === 'editor' ? (
                <WorkflowDefinitionEditor
                  definitionId={selectedDefinitionId}
                  onBack={returnToDefinitionBrowser}
                  initialStateCode={editorInitialState}
                  initialPracticeArea={editorInitialPA}
                />
              ) : (
                <WorkflowDefinitionBrowser
                  onEditDefinition={openDefinitionEditor}
                  onCreateNew={openNewDefinition}
                />
              )}
            </>
          )}

          {/* ─── Coverage Matrix Tab ─── */}
          {activeTab === 'coverage' && (
            <div
              className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm"
              style={{ animation: 'fadeSlideIn 300ms ease-out forwards' }}
            >
              <WorkflowCoverageDashboard
                onOpenDefinition={(defId) => {
                  // Pre-set statutory sub-view, then switch tab without resetting
                  setSelectedDefinitionId(defId);
                  setStatutoryView('editor');
                  setEditorInitialState(undefined);
                  setEditorInitialPA(undefined);
                  skipStatutoryResetRef.current = true;
                  switchTab('statutory');
                }}
                onCreateDefinition={(stateCode, practiceArea) => {
                  // Pre-set statutory sub-view with pre-populated fields, then switch tab
                  setSelectedDefinitionId(null);
                  setStatutoryView('editor');
                  setEditorInitialState(stateCode);
                  setEditorInitialPA(practiceArea);
                  skipStatutoryResetRef.current = true;
                  switchTab('statutory');
                }}
              />
            </div>
          )}

          {/* ─── Archived Tab ─── */}
          {activeTab === 'archived' && (
            <ArchivedTemplates />
          )}
        </div>
      </div>

      {/* ─── Archive Template Modal ─── */}
      {archiveTarget && (
        <ArchiveTemplateModal
          template={archiveTarget}
          onClose={() => setArchiveTarget(null)}
          onArchived={handleArchived}
        />
      )}
      {showCreateBaseModal && (
        <CreateJurisdictionBaseModal
          onClose={() => setShowCreateBaseModal(false)}
          onCreated={(id) => {
            setShowCreateBaseModal(false);
            setSelectedTemplateId(id);
            setTemplateView('editor');
          }}
        />
      )}
    </>
  );
};

export default TaskTemplatesPanel;
